import { Outlet } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'
import { useState, useEffect, useCallback } from 'react'
import { Sidebar } from './Sidebar'
import { Header } from './Header'
import { ConnectionStatus } from '../features/ConnectionStatus'
import { IncomingCallNotification } from '../ui/IncomingCallNotification'
import { ProactivePermissions } from '../ui/ProactivePermissions'
import { useP2PStore } from '../../store/p2pStore'
import { toast, TOAST_GROUPS } from '../../utils/toastManager'

export function MainLayout() {
  const navigate = useNavigate()
  const store = useP2PStore()
  const {
    connections,
    mediaService,
    localSelfta
  } = store

  // Convert connections Map to Array
  const connectionsArray = Array.from(connections.values())

  // Incoming call state - moved from EnhancedChatPage to global level
  const [incomingCall, setIncomingCall] = useState<{
    isVisible: boolean
    callType: 'voice' | 'video'
    fromPeer: string
    fromAlias: string
    mediaCall?: any // Store the WebRTC MediaCall object for bidirectional audio
  } | null>(null)

  // Subscribe to media service events (global level)
  useEffect(() => {
    if (!mediaService) return

    const handleIncomingCall = (eventData: any) => {
      // Extract data from EventEmitter wrapper structure
      const data = eventData?.data || eventData

      console.log('📞 GLOBAL: Incoming call RAW:', eventData)
      console.log('📞 GLOBAL: Incoming call EXTRACTED:', data)
      console.log('🚨 GLOBAL: MediaCall object present:', !!data.mediaCall)

      // Enhanced fallback generation that maintains Unknown caller name resolution
      let resolvedAlias = data.fromAlias
      let resolvedPeerId = data.fromPeerId

      // Only generate fallbacks when absolutely necessary
      const shouldGenerateFallback = !resolvedAlias || resolvedAlias === 'Anonymous_Caller' || resolvedAlias?.includes('Unknown')

      if (shouldGenerateFallback) {
        // Use peer ID as display name if available
        if (resolvedPeerId && resolvedPeerId !== 'unknown_peer' && !resolvedPeerId.includes('caller_')) {
          resolvedAlias = resolvedPeerId
          console.log(`✅ GLOBAL: Using peer ID as display name: ${resolvedAlias}`)
        } else {
          // Only generate random names as last resort
          resolvedAlias = `Caller_${Date.now().toString().slice(-6)}`
          console.warn(`⚠️ GLOBAL: Generated random alias fallback: ${resolvedAlias}`)
        }
      }

      // Only generate random peer IDs if we truly have no peer information
      if (!resolvedPeerId || resolvedPeerId === 'unknown_peer') {
        resolvedPeerId = `caller_${Date.now().toString().slice(-6)}`
        console.warn(`⚠️ GLOBAL: Generated random peer ID fallback: ${resolvedPeerId}`)
      }

      setIncomingCall({
        isVisible: true,
        callType: data.callType,
        fromPeer: resolvedPeerId,
        fromAlias: resolvedAlias,
        mediaCall: data.mediaCall // Store the MediaCall object for bidirectional audio
      })
    }

    // Subscribe to incoming call events
    mediaService.on('incomingCall', handleIncomingCall)

    return () => {
      mediaService.removeAllListeners('incomingCall')
    }
  }, [mediaService])

  // Accept incoming call
  const handleAcceptCall = useCallback(async () => {
    if (!incomingCall || !mediaService || !localSelfta) return

    const peerConnection = connectionsArray.find(c => c.id === incomingCall.fromPeer)
    if (!peerConnection?.connection) {
      toast.error('Cannot accept call: Peer not connected', TOAST_GROUPS.CALL)
      setIncomingCall(null)
      return
    }

    // Find caller peer data for avatar (same logic as JSX rendering)
    const callerPeerData = connectionsArray.find(c => {
      // Try exact peerId match first (most reliable)
      if (c.peerId === incomingCall.fromPeer) return true
      // Try id match
      if (c.id === incomingCall.fromPeer) return true
      return false
    })

    try {
      console.log('🚨 GLOBAL: Accepting call with mediaCall:', !!incomingCall.mediaCall)
      if (incomingCall.mediaCall) {
        console.log('🚨 GLOBAL: MediaCall object at acceptance:', Object.keys(incomingCall.mediaCall))
      }

      const success = await mediaService.acceptCall(
        incomingCall.callType,
        incomingCall.fromPeer,
        incomingCall.fromAlias,
        peerConnection.connection,
        undefined, // localVideoElement - not needed here
        incomingCall.mediaCall, // Pass the MediaCall object for bidirectional audio
        // CRITICAL FIX: Clean string "null" to actual null for caller avatar
        (callerPeerData?.selfta?.profileImage === "null" ||
         callerPeerData?.selfta?.profileImage === null ||
         callerPeerData?.selfta?.profileImage === undefined ||
         callerPeerData?.selfta?.profileImage === "") ? null : callerPeerData?.selfta?.profileImage
      )

      if (success) {
        // Navigate to chat page for call management
        navigate('/chat')

        toast.success(`${incomingCall.callType === 'video' ? 'Video' : 'Voice'} call accepted`, TOAST_GROUPS.CALL)
      }
    } catch (error) {
      console.error('GLOBAL: Failed to accept call:', error)
      toast.error('Failed to accept call', TOAST_GROUPS.CALL)
    }

    setIncomingCall(null)
  }, [incomingCall, mediaService, localSelfta, connectionsArray, navigate])

  // Decline incoming call
  const handleDeclineCall = useCallback(() => {
    if (!incomingCall || !mediaService) return

    const peerConnection = connectionsArray.find(c => c.id === incomingCall.fromPeer)
    if (peerConnection?.connection) {
      mediaService.rejectCall(
        incomingCall.callType,
        incomingCall.fromPeer,
        incomingCall.fromAlias,
        peerConnection.connection
      )
    }

    // Close the MediaCall object when declining
    if (incomingCall.mediaCall) {
      try {
        incomingCall.mediaCall.close()
        console.log('🚫 GLOBAL: MediaCall closed after decline')
      } catch (error) {
        console.warn('⚠️ GLOBAL: Error closing MediaCall after decline:', error)
      }
    }

    setIncomingCall(null)
  }, [incomingCall, mediaService, connectionsArray])

  return (
    <>
      <div
        className="flex bg-secondary-50"
        style={{
          minHeight: '100dvh', // Dynamic viewport height for mobile (with fallback in CSS)
          height: '100dvh',
        }}
      >
        {/* Sidebar */}
        <aside className="hidden md:flex md:w-64 md:flex-col">
          <Sidebar />
        </aside>

        {/* Main Content Area */}
        <div className="flex flex-1 flex-col overflow-hidden">
          <Header />

          <main
            className="flex-1 overflow-y-auto bg-white"
            style={{
              // Handle safe areas and keyboard on mobile
              paddingBottom: 'env(safe-area-inset-bottom)',
            }}
          >
            <div className="h-full min-h-0">
              <Outlet />
            </div>
          </main>

          {/* Connection Status Bar */}
          <ConnectionStatus />
        </div>
      </div>

      {/* CRITICAL FIX: Global Incoming Call Notification - Always Visible Regardless of Route */}
      {incomingCall && (() => {
        // Find caller peer data for enhanced display - try multiple matching strategies
        const callerPeerData = connectionsArray.find(c => {
          // Try exact peerId match first (most reliable)
          if (c.peerId === incomingCall.fromPeer) return true
          // Try id match
          if (c.id === incomingCall.fromPeer) return true
          // Try selfta alias match (less reliable but sometimes needed)
          if (c.selfta?.alias === incomingCall.fromAlias) return true
          // Try connection-level alias match
          if (c.alias === incomingCall.fromAlias) return true
          return false
        })

        // Use the SAME data source as ConnectionsPage (which works perfectly)
        // Priority: connection.selfta?.alias (primary) -> connection.alias -> MediaService resolved alias -> guaranteed fallback
        const resolvedCallerName = callerPeerData?.selfta?.alias ||
                                  callerPeerData?.alias ||
                                  incomingCall.fromAlias ||
                                  (incomingCall.fromPeer && incomingCall.fromPeer.length <= 12
                                    ? incomingCall.fromPeer
                                    : `Caller_${incomingCall.fromPeer ? incomingCall.fromPeer.substring(0, 8) : 'Anonymous'}`)

        console.log('🔍 GLOBAL MAINLAYOUT - CALLER NAME RESOLUTION:')
        console.log('  📞 incomingCall object:', {
          ...incomingCall,
          fromPeerType: typeof incomingCall.fromPeer,
          fromAliasType: typeof incomingCall.fromAlias,
          fromPeerLength: incomingCall.fromPeer?.length,
          fromAliasLength: incomingCall.fromAlias?.length
        })
        console.log('  👥 callerPeerData found:', {
          found: !!callerPeerData,
          peerId: callerPeerData?.peerId,
          id: callerPeerData?.id,
          alias: callerPeerData?.alias,
          selftaAlias: callerPeerData?.selfta?.alias,
          displayName: callerPeerData?.displayName,
          selftaDisplayName: callerPeerData?.selfta?.displayName,
          hasAvatar: !!callerPeerData?.selfta?.profileImage,
          avatarPreview: callerPeerData?.selfta?.profileImage ?
            `${callerPeerData.selfta.profileImage.substring(0, 50)}...` : 'none'
        })
        console.log('  ✅ GLOBAL FINAL RESOLVED NAME:', resolvedCallerName)

        return (
          <IncomingCallNotification
            isVisible={incomingCall.isVisible}
            callType={incomingCall.callType}
            callerName={resolvedCallerName}
            callerPeer={{
              ...(callerPeerData?.selfta?.displayName || callerPeerData?.displayName ? {
                displayName: callerPeerData?.selfta?.displayName || callerPeerData?.displayName
              } : {}),
              ...(callerPeerData?.selfta?.alias || callerPeerData?.alias || incomingCall.fromAlias ? {
                alias: callerPeerData?.selfta?.alias || callerPeerData?.alias || incomingCall.fromAlias
              } : {}),
              peerId: callerPeerData?.peerId || incomingCall.fromPeer,
              id: callerPeerData?.id || incomingCall.fromPeer
            }}
            callerAvatar={callerPeerData?.selfta?.profileImage || undefined}
            onAccept={handleAcceptCall}
            onDecline={handleDeclineCall}
            autoDeclineTimeout={30}
            enableAutoNavigation={true}
          />
        )
      })()}

      {/* DESKTOP/iOS SAFARI: Proactive Permission Requests */}
      <ProactivePermissions />
    </>
  )
}